import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CEODashboard from "@/pages/CEODashboard";
import SafetyVault from "@/pages/SafetyVault";
import AdminSecretsExport from "@/pages/AdminSecretsExport";
import DivisionDetail from "@/pages/DivisionDetail";
import WeeklyUpdateForm from "@/pages/WeeklyUpdateForm";
import AdminPanel from "@/pages/AdminPanel";
import MirrorPage from "@/pages/MirrorPage";
import OriginStoryPage from "@/pages/OriginStoryPage";
import MiraChat from "@/pages/MiraChat";
import ValuesAudit from "@/pages/ValuesAudit";
import IPRegister from "@/pages/IPRegister";
import PersonProfile from "@/pages/PersonProfile";
import HRHubPage from '@/pages/HRHubPage';
import MyProfileRedirect from '@/pages/MyProfileRedirect';
import BookingPipeline from "@/pages/BookingPipeline";
import SystemDocs from "@/pages/SystemDocs";
import CompleteArchitecturePage from "@/pages/CompleteArchitecturePage";
import ShopifyFlowSetupPage from "@/pages/ShopifyFlowSetupPage";
import GroupSalesPage from "@/pages/GroupSalesPage";
import GroupFinancialsPage from "@/pages/GroupFinancialsPage";
import TrainingLaunchSummary from "@/pages/TrainingLaunchSummary";
import MyTrainingPage from "@/pages/MyTrainingPage";
import RadioListenPage from "@/pages/RadioListenPage";
import PublicSoulForm from "@/pages/PublicSoulForm";
import OutletIntelPage from "@/pages/OutletIntelPage";
import OutletIntelligencePage from "@/pages/OutletIntelligencePage";
import OrgChartPage from "@/pages/OrgChartPage";
import ClientDossierPage from "@/pages/ClientDossierPage";
import QuickStartGuide from "@/pages/QuickStartGuide";
import ClientIntelligenceHub from "@/pages/ClientIntelligenceHub";
import CustomerIntelligenceAdmin from "@/pages/CustomerIntelligenceAdmin";
import MemberCareWorkbench from "@/pages/MemberCareWorkbench";
import NewsletterEditorPage from "@/pages/NewsletterEditorPage";
import NewsletterPublicPage from "@/pages/NewsletterPublicPage";
import TDBCostModule from "@/pages/TDBCostModule";
import TDBIngredientMasterPage from "@/pages/TDBIngredientMasterPage";
import TDBRecipeEditorPage from "@/pages/TDBRecipeEditorPage";
import TDBBundleBOMPage from "@/pages/TDBBundleBOMPage";
import XiaomiShell from "@/pages/XiaomiShell";
import XiaomiDashboard from "@/pages/XiaomiDashboard";
import RedBerylImportPage from "@/pages/RedBerylImportPage";
import SoulEnrichmentPage from "@/pages/SoulEnrichmentPage";
import DreamWeaverForm from "@/pages/DreamWeaverForm";
import ProgrammePulsePage from "@/pages/ProgrammePulsePage";
import ClientMisPage from "@/pages/ClientMisPage";
import DisasterRecoveryDrill from "@/pages/DisasterRecoveryDrill";
import XiaomiBenefitBoard from "@/pages/XiaomiBenefitBoard";
import XiaomiAdminTemplates from "@/pages/XiaomiAdminTemplates";
import XiaomiAdminDealers from "@/pages/XiaomiAdminDealers";
import XiaomiAdminBenefits from "@/pages/XiaomiAdminBenefits";
import XiaomiAdminZohoBridge from "@/pages/XiaomiAdminZohoBridge";
import XiaomiGiftOps from "@/pages/XiaomiGiftOps";
import XiaomiAyushPush from "@/pages/XiaomiAyushPush";
import XiaomiPartner360 from "@/pages/XiaomiPartner360";
import XiaomiPartners from "@/pages/XiaomiPartners";
import XiaomiPartnerJourneyPage from "@/pages/XiaomiPartnerJourneyPage";
import XiaomiUnwrapped from "@/pages/XiaomiUnwrapped";
import XiaomiMatrix from "@/pages/XiaomiMatrix";
import XiaomiDBR from "@/pages/XiaomiDBR";
import XiaomiApprovals from "@/pages/XiaomiApprovals";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { canAccessClientIntel, isHrHomeUser } from "@/lib/permissions";

function ProtectedRoute({ children, ceoOnly = false, conciergeTeam = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F5F2' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p style={{ color: '#1B2A4A' }}>Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (ceoOnly && user.role !== 'ceo') return <Navigate to={`/division/${user.division_id}`} replace />;
  if (conciergeTeam && !canAccessClientIntel(user)) return <Navigate to={`/division/${user.division_id}`} replace />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPublic = path.startsWith('/soul/') || path.startsWith('/dreamweaver') || path.startsWith('/r/') || path.startsWith('/c/') || path.startsWith('/forgot-password') || path.startsWith('/reset-password');

  if (loading && !isPublic) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F5F2' }}>
        <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const homeRedirect = () => {
    if (!user) return <Navigate to="/login" replace />;
    if (user.must_change_password) return <Navigate to="/change-password" replace />;
    if (user.role === 'ceo') return <Navigate to="/dashboard" replace />;
    if (isHrHomeUser(user)) return <Navigate to="/people" replace />;
    if (user.role === 'cost_controller') return <Navigate to={`/division/${user.division_id || 'me'}`} replace />;
    // Sub-leader with accommodation-only scope → land on bookings
    if (user.role === 'sub_leader' && user.sub_view?.has_bookings && user.sub_view?.scope_value?.length === 1 && user.sub_view.scope_value[0]?.includes('Stays')) {
      return <Navigate to="/bookings" replace />;
    }
    return <Navigate to={`/division/${user.division_id}`} replace />;
  };

  return (
    <Routes>
      {/* PUBLIC — no login needed. Tokenized staff listen page. */}
      <Route path="/r/:token" element={<RadioListenPage />} />
      {/* PUBLIC — branded Soul Form (token-authenticated, no login). */}
      <Route path="/soul/:token" element={<PublicSoulForm />} />
      <Route path="/dreamweaver" element={<DreamWeaverForm />} />
      <Route path="/c/:token" element={<NewsletterPublicPage />} />
      <Route path="/login" element={        user
          ? (user.must_change_password
              ? <Navigate to="/change-password" replace />
              : (user.role === 'ceo' ? <Navigate to="/dashboard" replace />
                : user.role === 'cost_controller' ? <Navigate to={`/division/${user.division_id || 'me'}`} replace />
                : (user.role === 'sub_leader' && user.sub_view?.has_bookings && user.sub_view?.scope_value?.length === 1 && user.sub_view?.scope_value?.[0]?.includes('Stays'))
                  ? <Navigate to="/bookings" replace />
                  : <Navigate to={`/division/${['cc-clubs', 'cc-ops'].includes(user.division_id) ? 'cc' : user.division_id}`} replace />))
          : <LoginPage />
      } />
      <Route path="/change-password" element={user ? <ChangePasswordPage /> : <Navigate to="/login" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute ceoOnly><Layout><CEODashboard /></Layout></ProtectedRoute>} />
      {/* TDC pattern 20-Jun-2026 · one-button backup page for the founder.
         Lives outside Layout so it's the chrome-free "single front door"
         the pattern calls for.  CEO-only. */}
      <Route path="/safety-vault" element={<ProtectedRoute ceoOnly><SafetyVault /></ProtectedRoute>} />
      {/* TDC pattern 20-Jun-2026 · one-time secrets export.  Re-auth
         required even though already logged in.  CEO-only. */}
      <Route path="/admin/secrets/export" element={<ProtectedRoute ceoOnly><AdminSecretsExport /></ProtectedRoute>} />
      <Route path="/division/:divId" element={<ProtectedRoute><Layout><DivisionDetail /></Layout></ProtectedRoute>} />
      <Route path="/division/:divId/outlet/:outletId" element={<ProtectedRoute><Layout><OutletIntelPage /></Layout></ProtectedRoute>} />
      <Route path="/outlet-intelligence" element={<ProtectedRoute><Layout><OutletIntelligencePage /></Layout></ProtectedRoute>} />
      <Route path="/update" element={<ProtectedRoute><Layout><WeeklyUpdateForm /></Layout></ProtectedRoute>} />
      <Route path="/update/:divId" element={<ProtectedRoute><Layout><WeeklyUpdateForm /></Layout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute ceoOnly><Layout><AdminPanel /></Layout></ProtectedRoute>} />
      <Route path="/mirror" element={<ProtectedRoute><Layout><MirrorPage /></Layout></ProtectedRoute>} />
      <Route path="/mirror/:userId" element={<ProtectedRoute><Layout><MirrorPage /></Layout></ProtectedRoute>} />
      <Route path="/our-story" element={<ProtectedRoute><Layout><OriginStoryPage /></Layout></ProtectedRoute>} />
      <Route path="/mira" element={<ProtectedRoute><Layout><MiraChat /></Layout></ProtectedRoute>} />
      <Route path="/values" element={<ProtectedRoute><Layout><ValuesAudit /></Layout></ProtectedRoute>} />

      {/* ─── Xiaomi 2026 · Sprint 1 routes (XiaomiShell · no Layout wrapper) ─── */}
      <Route path="/xiaomi" element={<ProtectedRoute><XiaomiShell><XiaomiDashboard /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/board/:lifecycleKey" element={<ProtectedRoute><XiaomiShell><XiaomiBenefitBoard /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/admin/templates" element={<ProtectedRoute><XiaomiShell><XiaomiAdminTemplates /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/partners" element={<ProtectedRoute><XiaomiShell><XiaomiPartners /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/admin/dealers" element={<ProtectedRoute><XiaomiShell><XiaomiAdminDealers /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/admin/benefits" element={<ProtectedRoute><XiaomiShell><XiaomiAdminBenefits /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/admin/zoho-bridge" element={<ProtectedRoute><XiaomiShell><XiaomiAdminZohoBridge /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/gift-ops" element={<ProtectedRoute><XiaomiShell><XiaomiGiftOps /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/ayush/anniversary-push" element={<ProtectedRoute><XiaomiShell><XiaomiAyushPush /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/partners/:retailerId" element={<ProtectedRoute><XiaomiShell><XiaomiPartner360 /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/partners/:retailerId/journey/:benefitId/:monthYm" element={<ProtectedRoute><XiaomiShell><XiaomiPartnerJourneyPage /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/unwrapped" element={<ProtectedRoute><XiaomiShell><XiaomiUnwrapped /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/unwrapped/:retailer_id" element={<ProtectedRoute><XiaomiShell><XiaomiUnwrapped /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/matrix" element={<ProtectedRoute><XiaomiShell><XiaomiMatrix /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/dbrs" element={<ProtectedRoute><XiaomiShell><XiaomiDBR /></XiaomiShell></ProtectedRoute>} />
      <Route path="/xiaomi/approvals" element={<ProtectedRoute><XiaomiShell><XiaomiApprovals /></XiaomiShell></ProtectedRoute>} />
      <Route path="/ip-register" element={<ProtectedRoute ceoOnly><Layout><IPRegister /></Layout></ProtectedRoute>} />
      <Route path="/profile/:email" element={<ProtectedRoute><Layout><MyProfileRedirect /></Layout></ProtectedRoute>} />
      <Route path="/people" element={<ProtectedRoute><Layout><HRHubPage /></Layout></ProtectedRoute>} />
      <Route path="/people/:personId" element={<ProtectedRoute><Layout><PersonProfile /></Layout></ProtectedRoute>} />
      <Route path="/client/:clientId" element={<ProtectedRoute><Layout><ClientDossierPage /></Layout></ProtectedRoute>} />
      <Route path="/guide" element={<ProtectedRoute><Layout><QuickStartGuide /></Layout></ProtectedRoute>} />
      <Route path="/tdb/cost" element={<ProtectedRoute><Layout><TDBCostModule /></Layout></ProtectedRoute>} />
      <Route path="/tdb/ingredient-master" element={<ProtectedRoute><Layout><TDBIngredientMasterPage /></Layout></ProtectedRoute>} />
      <Route path="/tdb/recipes" element={<ProtectedRoute><Layout><TDBRecipeEditorPage /></Layout></ProtectedRoute>} />
      <Route path="/tdb/bundles" element={<ProtectedRoute><Layout><TDBBundleBOMPage /></Layout></ProtectedRoute>} />
      <Route path="/my-training" element={<ProtectedRoute><Layout><MyTrainingPage /></Layout></ProtectedRoute>} />
      <Route path="/org" element={<ProtectedRoute><Layout><OrgChartPage /></Layout></ProtectedRoute>} />
      <Route path="/cost" element={<ProtectedRoute><Layout><Navigate to="/division/me" replace /></Layout></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute><Layout><BookingPipeline /></Layout></ProtectedRoute>} />
      <Route path="/docs" element={<ProtectedRoute><Layout><SystemDocs /></Layout></ProtectedRoute>} />
      <Route path="/docs/complete-architecture" element={<ProtectedRoute><Layout><CompleteArchitecturePage /></Layout></ProtectedRoute>} />
      <Route path="/docs/shopify-flow-setup" element={<ProtectedRoute><Layout><ShopifyFlowSetupPage /></Layout></ProtectedRoute>} />
      <Route path="/sales-group" element={<ProtectedRoute><Layout><GroupSalesPage /></Layout></ProtectedRoute>} />
      <Route path="/group-financials" element={<ProtectedRoute><Layout><GroupFinancialsPage /></Layout></ProtectedRoute>} />
      <Route path="/training/launch-summary" element={<ProtectedRoute><Layout><TrainingLaunchSummary /></Layout></ProtectedRoute>} />
      <Route path="/client-intelligence" element={<ProtectedRoute conciergeTeam><Layout><ClientIntelligenceHub /></Layout></ProtectedRoute>} />
      <Route path="/client-intelligence/:clientId" element={<ProtectedRoute conciergeTeam><Layout><ClientIntelligenceHub /></Layout></ProtectedRoute>} />
      <Route path="/admin/ci" element={<ProtectedRoute><Layout><CustomerIntelligenceAdmin /></Layout></ProtectedRoute>} />
      <Route path="/member-care/workbench" element={<ProtectedRoute conciergeTeam><Layout><MemberCareWorkbench /></Layout></ProtectedRoute>} />
      <Route path="/member-care/workbench/:clientId" element={<ProtectedRoute conciergeTeam><Layout><MemberCareWorkbench /></Layout></ProtectedRoute>} />
      <Route path="/redberyl/import" element={<ProtectedRoute conciergeTeam><Layout><RedBerylImportPage /></Layout></ProtectedRoute>} />
      <Route path="/soul-enrichment" element={<ProtectedRoute conciergeTeam><Layout><SoulEnrichmentPage /></Layout></ProtectedRoute>} />
      <Route path="/programme-pulse" element={<ProtectedRoute conciergeTeam><Layout><ProgrammePulsePage /></Layout></ProtectedRoute>} />
      <Route path="/client-mis/:clientId" element={<ProtectedRoute conciergeTeam><Layout><ClientMisPage /></Layout></ProtectedRoute>} />
      <Route path="/disaster-recovery" element={<ProtectedRoute ceoOnly><Layout><DisasterRecoveryDrill /></Layout></ProtectedRoute>} />
      <Route path="/newsletters/:id" element={<ProtectedRoute><Layout><NewsletterEditorPage /></Layout></ProtectedRoute>} />
      <Route path="/" element={homeRedirect()} />
      <Route path="*" element={homeRedirect()} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
